/**
 * The strategy deck (strategy v3, Figma 13 Strategy, Zone plate · APPROVED):
 * the filmstrip on the left, the slide in the middle, or every slide as a
 * grid under its chapter heads. Creo sits in the shell's rail beside it. The
 * page hands in the toolbar (title, status, the primary action) and owns every
 * mutation; the client's read-only deck passes none of them.
 *
 * Slides are the saved document drawn through buildStrategySlides and the 42
 * templates; the deck never rewrites content. Dev builds keep the `<>` switch
 * that colours the deck's fixed copy pink and the strategy's values green (D3).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { animate, useReducedMotion } from "motion/react";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import type { StrategyDocument } from "@/types/strategyDocument.type";
import type { StrategySlideImages, StrategySlideMedia } from "@/types/strategy.type";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { SPRING_GENTLE } from "@/lib/motion";
import { isDevMode } from "@/config/envvars";
import { KitIconButton } from "../../components/kit/IconButton";
import { KitModal } from "../../components/kit/Modal";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import { MediaPickerBody, type MediaPickerTab } from "../../components/media/MediaPickerBody";
import { ExternalMediaPicker } from "../../components/media/ExternalMediaPicker";
import { SlideMediaWell, type MediaSlotControls } from "./SlideMediaWell";
import { stablePictureUrls, stableSlideImages } from "./stableSlideImages";
import { buildStrategySlides, type StrategySlide } from "./strategySlides";
import type { StrategySlideMediaVars } from "../api/useStrategies";
import { CHAPTER_COUNT, chapterSpec, groupLabel, groupOf, periodYears, shapeDeck, slideNumber } from "../deck/deckModel";
import { DeckViewSwitch, LazyMiniSlide, MiniSlide, useBox, type DeckView } from "../deck/DeckParts";
import { StrategyTemplateSlide } from "../templates/StrategyTemplates";
import type { TemplateContext } from "../templates/kit";
import { fixedLabels, selectionSource, type SlideTextSource } from "./slideTextSource";
import "./strategySlides.css";
import "../deck/deck.css";

export type { StrategySlideImages, StrategySlideMedia };
export type { DeckView };

export interface StrategyDocumentMeta {
  brand?: string | null;
  title?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  /** The strategy row's status, version and dates, for the record. */
  record?: { status: string; version: string; liveAt?: string | null; updatedAt?: string | null };
}

export interface StrategyDocumentViewProps {
  doc: StrategyDocument;
  meta?: StrategyDocumentMeta;
  slideImages?: StrategySlideImages;
  /** Fresh links for the document's library pictures ("media:<id>"). */
  pictureUrls?: Record<string, string>;
  selectedSlideId?: string | null;
  selectedSection?: string | null;
  onSlideChange?: (slide: StrategySlide) => void;
  /** Text the person highlighted on the open slide, for the agent's composer, and whether it is the deck's fixed copy or the strategy's; null when it is let go. */
  onTextSelect?: (text: string | null, source?: SlideTextSource) => void;
  /** The open slide's fixed labels (the deck's own copy), for the agent's context. */
  onSlideLabels?: (labels: string[]) => void;
  onMediaChange?: (vars: StrategySlideMediaVars) => Promise<unknown>;
  brandId?: string;
  /** Slide view or grid view; the page keeps it in the URL. */
  view?: DeckView;
  onViewChange?: (view: DeckView) => void;
  /** The rest of the toolbar after the view switch: title, status, actions. */
  toolbar?: (info: { slideCount: number }) => ReactNode;
  presenting?: boolean;
  onPresentingChange?: (open: boolean) => void;
  /** The deck's length, for the page's own lines ("Drafted. 62 slides."). */
  onSlideCount?: (count: number) => void;
}

const NO_SLIDE_IMAGES: StrategySlideImages = {};
const NO_PICTURE_URLS: Record<string, string> = {};

const StrategyDocumentView: FC<StrategyDocumentViewProps> = ({ doc, meta, slideImages: liveSlideImages = NO_SLIDE_IMAGES, pictureUrls: livePictureUrls = NO_PICTURE_URLS, selectedSlideId, selectedSection, onSlideChange, onTextSelect, onSlideLabels, onMediaChange, brandId, view: controlledView, onViewChange, toolbar, presenting: controlledPresenting, onPresentingChange, onSlideCount }) => {
  const slideImages = useMemo(() => stableSlideImages(liveSlideImages), [liveSlideImages]);
  const pictureUrls = useMemo(() => stablePictureUrls(livePictureUrls), [livePictureUrls]);
  const allSlides = useMemo(() => buildStrategySlides(doc), [doc]);
  const shape = useMemo(() => shapeDeck(allSlides), [allSlides]);
  const [localId, setLocalId] = useState<string | null>(null);
  const requestedId = selectedSlideId ?? localId;
  const slide = allSlides.find((s) => s.id === requestedId) ?? allSlides.find((s) => s.section === selectedSection) ?? allSlides[0];
  const sequence = shape.story;
  const index = sequence.indexOf(slide);
  const slideCountRef = useRef(onSlideCount);
  useEffect(() => { slideCountRef.current = onSlideCount; });
  useEffect(() => { slideCountRef.current?.(shape.story.length); }, [shape.story.length]);

  const [internalView, setInternalView] = useState<DeckView>("slides");
  const view = controlledView ?? internalView;
  const [internalPresenting, setInternalPresenting] = useState(false);
  const presenting = controlledPresenting ?? internalPresenting;
  const setPresenting = (open: boolean) => { if (onPresentingChange) onPresentingChange(open); else setInternalPresenting(open); };
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [mediaSlot, setMediaSlot] = useState<string | null>(null);
  const [source, setSource] = useState("team");
  const [mediaTab, setMediaTab] = useState<MediaPickerTab>("library");
  // Dev builds only: colour the strategy's own text apart from the deck's copy.
  const [showSources, setShowSources] = useState(false);
  const sourcesClass = isDevMode && showSources ? " st-sources-on" : "";
  const phone = useMediaQuery("(max-width: 639px)");
  const reduced = useReducedMotion();
  useEffect(() => { onSlideChange?.(slide); }, [slide, onSlideChange]); // Content refreshes must not reset a selection.

  /* --- the filmstrip's open groups: the one in view, plus any the person opened. */
  const inGroup = groupOf(shape, slide);
  const currentGroup = slide.group;
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set([currentGroup]));
  const [lastGroup, setLastGroup] = useState(currentGroup);
  if (lastGroup !== currentGroup) {
    setLastGroup(currentGroup);
    if (!openGroups.has(currentGroup)) setOpenGroups(new Set([...openGroups, currentGroup]));
  }
  const toggleGroup = (id: string) => setOpenGroups((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  /* --- motion: switching views flies the slide between its places. */
  const flipFrom = useRef<DOMRect | null>(null);
  const stageSlideRef = useRef<HTMLDivElement>(null);
  // Once the slide is drawn, the agent's context learns which of its words are the deck's fixed labels.
  useEffect(() => {
    if (!onSlideLabels) return;
    const root = stageSlideRef.current;
    onSlideLabels(view === "slides" && root ? fixedLabels(root) : []);
  }, [slide, view, onSlideLabels]);
  const gridRef = useRef<HTMLDivElement>(null);
  const [entering, setEntering] = useState<DeckView | null>(null);
  useLayoutEffect(() => {
    const from = flipFrom.current;
    flipFrom.current = null;
    if (!from) return;
    const target = view === "slides"
      ? stageSlideRef.current
      : gridRef.current?.querySelector<HTMLElement>(`[data-slide-id="${CSS.escape(slide.id)}"] .sd-ring`) ?? null;
    if (!target) return;
    if (view === "grid") target.scrollIntoView({ block: "nearest" });
    const to = target.getBoundingClientRect();
    if (!to.width) return;
    if (reduced) { animate(target, { opacity: [0, 1] }, { duration: 0.16 }); return; }
    target.style.transformOrigin = "0 0";
    animate(target, { x: [from.left - to.left, 0], y: [from.top - to.top, 0], scale: [from.width / to.width, 1] }, view === "slides" ? SPRING_GENTLE : { duration: 0.26, ease: "easeInOut" });
  }, [slide.id, view, reduced]);
  useEffect(() => {
    if (!entering) return;
    const id = window.setTimeout(() => setEntering(null), 420);
    return () => window.clearTimeout(id);
  }, [entering]);
  const changeView = (next: DeckView, from?: DOMRect | null) => {
    if (next === view) return;
    const origin = from ?? (view === "slides" ? stageSlideRef.current?.getBoundingClientRect() : gridRef.current?.querySelector<HTMLElement>('[aria-current="true"] .sd-ring')?.getBoundingClientRect()) ?? null;
    flipFrom.current = origin;
    setEntering(next);
    if (onViewChange) onViewChange(next); else setInternalView(next);
  };

  const select = (next: StrategySlide) => {
    if (saving) return;
    setLocalId(next.id);
    onSlideChange?.(next);
    setStatus(""); setError("");
  };
  const move = (offset: number) => {
    const next = sequence[index + offset];
    if (next) select(next);
  };
  useEffect(() => {
    if (!presenting || saving) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement).closest("input, textarea, video, audio, select")) return;
      const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
      const next = offset ? sequence[index + offset] : undefined;
      if (!next) return;
      event.preventDefault();
      setLocalId(next.id);
      onSlideChange?.(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [presenting, saving, sequence, index, onSlideChange]);
  // Keep the selected thumbnail in view inside the filmstrip only.
  const filmRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = filmRef.current;
    const selected = list?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!list || !selected) return;
    const bounds = list.getBoundingClientRect();
    const item = selected.getBoundingClientRect();
    if (item.top < bounds.top) list.scrollTop += item.top - bounds.top - 8;
    else if (item.bottom > bounds.bottom) list.scrollTop += item.bottom - bounds.bottom + 8;
  }, [slide.id, view, openGroups]);

  const attach = async (vars: StrategySlideMediaVars) => {
    if (!onMediaChange) return;
    setMediaSlot(null); setSaving(true); setStatus("Saving the picture…"); setError("");
    try { await onMediaChange(vars); setStatus("Picture saved"); }
    catch { setError("Couldn’t save that picture. Choose it again to retry."); setStatus(""); }
    finally { setSaving(false); }
  };

  /** One slide, drawn at 1280 × 720. Thumbnails and Present draw it read-only. */
  const renderSlide = (s: StrategySlide, readOnly = false, present = false) => {
    const deckGroup = groupOf(shape, s);
    const canSetMedia = !readOnly && !!onMediaChange && !!brandId;
    // The team sees an empty picture well to fill; the client never does.
    const mayEditMedia = !present && !!onMediaChange && !!brandId;
    const controls = (slot: string, label: string): MediaSlotControls | undefined => canSetMedia ? {
      label, disabled: saving, onChoose: () => setMediaSlot(slot),
      onRemove: slideImages[slot] ? () => void attach({ slotKey: slot, mediaId: null }) : undefined,
    } : undefined;
    const ctx: TemplateContext = {
      doc,
      slide: s,
      brand: meta?.brand || meta?.title || "",
      periodStart: meta?.periodStart,
      periodEnd: meta?.periodEnd,
      years: periodYears(meta?.periodStart, meta?.periodEnd),
      page: slideNumber(shape, s),
      chapter: chapterSpec(s.chapter),
      // The opener counts the plate's thirteen chapters; Back closes on the last.
      chapterOf: { number: deckGroup?.spec.number ?? CHAPTER_COUNT, total: CHAPTER_COUNT, slides: deckGroup?.slides.length ?? 0 },
      record: meta?.record,
      picture: (slot, label, opts) => {
        const media = slideImages[slot];
        if (!media && opts?.fallbackUrl) return <img src={opts.fallbackUrl} alt={label} loading="lazy" />;
        if (!media && !mayEditMedia) return null;
        return <SlideMediaWell key={JSON.stringify(media ?? slot)} label={label} media={media} controls={readOnly ? undefined : controls(slot, label)} />;
      },
      media: (slot) => slideImages[slot],
      // A library picture ("media:<id>") until its link arrives shows nothing, never the raw reference.
      pictureUrl: (url) => (!url ? null : url.startsWith("media:") ? pictureUrls[url] ?? null : url),
      readOnly,
    };
    return (
      <article className="strategy-slide sv-host" aria-label={s.title}>
        <StrategyTemplateSlide ctx={ctx} />
      </article>
    );
  };

  /* --- the filmstrip --- */
  const numberOf = (s: StrategySlide) => slideNumber(shape, s);
  const filmSlide = (s: StrategySlide) => {
    const current = s.id === slide.id;
    return <button key={s.id} type="button" className="sd-film-slide" aria-current={current ? "true" : undefined} aria-label={`Slide ${numberOf(s)}: ${s.title}`} disabled={saving}
      onClick={() => select(s)}>
      <span className="sd-film-num">{numberOf(s)}</span>
      <span className="sd-ring"><span className="sd-thumb"><LazyMiniSlide width={154} render={() => renderSlide(s, true)} /></span></span>
    </button>;
  };
  const storyCount = shape.story.length;
  const position = `${index + 1} of ${storyCount}`;
  const filmstrip = <div className="sd-film-wrap">
    <nav className="sd-film" aria-label="Slides">
      <div className="sd-film-head"><strong>Slides</strong><span className="sd-film-count">{position}</span></div>
      <div ref={filmRef} className="sd-film-list">
        {shape.groups.map((group) => {
          const open = openGroups.has(group.spec.id);
          return <div key={group.spec.id} role="group" aria-label={groupLabel(group.spec)}>
            <button type="button" className="sd-film-chapter" aria-expanded={open} onClick={() => toggleGroup(group.spec.id)}>
              <ChevronRight aria-hidden strokeWidth={2} />
              <span className="sd-film-chapter-num">{group.spec.number ? String(group.spec.number).padStart(2, "0") : ""}</span>
              <span className="sd-film-chapter-title">{group.spec.name}</span>
              <span className="sd-film-chapter-status">{group.slides.length}</span>
            </button>
            {open && group.slides.map(filmSlide)}
          </div>;
        })}
      </div>
    </nav>
  </div>;

  /* --- the stage --- */
  const [stageRef, stageBox] = useBox<HTMLDivElement>();
  // The slide fills the stage's width and, on the desk, fits its height too.
  const slideWidth = Math.round(Math.max(240, phone ? stageBox.width : Math.min(stageBox.width - 12, ((stageBox.height - 120) * 16) / 9, 1200)));
  const chapterTitle = inGroup ? groupLabel(inGroup.spec) : "";
  const pager = <span className="sd-pager">
    <KitIconButton kind="ghost" size="sm" icon={ArrowLeft} aria-label="Previous slide" disabled={index <= 0 || saving} onClick={() => move(-1)} />
    <span aria-live="polite">{index + 1} / {sequence.length}</span>
    <KitIconButton kind="ghost" size="sm" icon={ArrowRight} aria-label="Next slide" disabled={index >= sequence.length - 1 || saving} onClick={() => move(1)} />
  </span>;
  const stage = <section ref={stageRef} className="sd-stage" tabIndex={-1} aria-label="Selected slide. Use the left and right arrow keys to move." onKeyDown={(event) => {
    if ((event.target as HTMLElement).closest("input, textarea, button, a, video")) return;
    if (event.key === "ArrowRight") { event.preventDefault(); move(1); }
    if (event.key === "ArrowLeft") { event.preventDefault(); move(-1); }
  }}>
    <div className="sd-slide-head" style={{ inlineSize: slideWidth }}>
      <p>Slide {numberOf(slide)} · {chapterTitle}</p>
      <span className="sd-push" />
    </div>
    <div ref={stageSlideRef} className="sd-slide" style={{ inlineSize: slideWidth, blockSize: (slideWidth * 9) / 16 }} onPointerUp={onTextSelect ? (event) => {
      // Highlighted words go to the agent's composer (as in an editor's chat);
      // a plain click on the slide lets the last highlight go.
      const area = event.currentTarget;
      window.setTimeout(() => {
        const picked = window.getSelection();
        const words = picked && !picked.isCollapsed && picked.anchorNode && area.contains(picked.anchorNode) ? picked.toString().replace(/\s+/g, " ").trim() : "";
        onTextSelect(words ? words.slice(0, 1200) : null, words && picked!.rangeCount ? selectionSource(picked!.getRangeAt(0), area) : undefined);
      });
    } : undefined}>
      <MiniSlide width={slideWidth}>{renderSlide(slide)}</MiniSlide>
    </div>
    <div className="sd-under" style={{ inlineSize: slideWidth }}>
      <p>Slide {numberOf(slide)} of {sequence.length} · {chapterTitle}</p>{pager}
    </div>
    {(status || error) && <div className="sd-notes" style={{ inlineSize: slideWidth }}>
      {error ? <p role="alert">{error}</p> : <p role="status">{status}</p>}
    </div>}
  </section>;

  /* --- the phone's strip under the slide --- */
  const phoneChapter = inGroup;
  const phoneStrip = phone && view === "slides" && <div className="sd-phone-strip">
    <p className="sd-phone-where"><b>{position}</b> {slide.title}</p>
    {phoneChapter && <p className="sd-phone-chapter"><span>{phoneChapter.spec.number ? String(phoneChapter.spec.number).padStart(2, "0") : ""}</span>{phoneChapter.spec.name}<span>{phoneChapter.slides.length}</span></p>}
    <div className="sd-phone-thumbs">{(phoneChapter?.slides ?? sequence).map((s) => <button key={s.id} type="button" className="sd-tile" aria-current={s.id === slide.id ? "true" : undefined} aria-label={`Slide ${numberOf(s)}: ${s.title}`} onClick={() => select(s)}>
      <span className="sd-ring"><span className="sd-thumb"><LazyMiniSlide width={94} render={() => renderSlide(s, true)} /></span></span>
      <span className="sd-tile-caption"><b>{numberOf(s)}</b></span>
    </button>)}</div>
    <p className="sd-phone-hint">Swipe the slide, or tap a thumbnail.</p>
  </div>;

  /* --- the grid --- */
  const tile = (s: StrategySlide) => (
    <button key={s.id} type="button" data-slide-id={s.id} className="sd-tile" aria-current={s.id === slide.id ? "true" : undefined} aria-label={`Slide ${numberOf(s)}: ${s.title}. Double-click to open.`}
      onClick={() => select(s)}
      onDoubleClick={(event) => { const from = event.currentTarget.querySelector(".sd-ring")?.getBoundingClientRect() ?? null; select(s); changeView("slides", from); }}
      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); const from = event.currentTarget.querySelector(".sd-ring")?.getBoundingClientRect() ?? null; select(s); changeView("slides", from); } }}>
      <span className="sd-ring"><span className="sd-thumb"><LazyMiniSlide width={phone ? 160 : 204} render={() => renderSlide(s, true)} /></span></span>
      <span className="sd-tile-caption"><b>{numberOf(s)}</b><span>{s.title}</span></span>
    </button>
  );
  const gridChapter = (key: string, title: string, statusText: string, slides: StrategySlide[], number?: number) => <section key={key} aria-label={title}>
    <div className="sd-grid-head">{number != null && <b>{String(number).padStart(2, "0")}</b>}<h2>{title}</h2><span>{statusText}</span></div>
    <div className="sd-grid-row">{slides.map(tile)}</div>
  </section>;
  const grid = <div ref={gridRef} className="sd-grid" aria-label="All slides">
    {shape.groups.map((group) => gridChapter(group.spec.id, group.spec.name, `${group.slides.length} slide${group.slides.length === 1 ? "" : "s"}`, group.slides, group.spec.number))}
  </div>;

  return <div className={`strategy-deck sd-workspace${sourcesClass}`} data-view={view} data-view-entering={entering ?? undefined}>
    {view === "slides" && !phone && filmstrip}
    <div className="sd-main">
      <div className="sd-toolbar">
        <DeckViewSwitch view={view} onChange={(next) => changeView(next)} />
        {toolbar?.({ slideCount: storyCount })}
      </div>
      {view === "slides" ? stage : grid}
      {phoneStrip}
      {/* Dev builds only: the deck's own copy in pink, the strategy's values in green. */}
      {isDevMode && <button type="button" className="st-sources-toggle" aria-pressed={showSources} title={showSources ? "Hide where the text comes from" : "Show where the text comes from: pink is the deck's copy, green is the strategy"} onClick={() => setShowSources((on) => !on)}>&lt;&gt;</button>}
    </div>
    <KitModal isOpen={presenting} onOpenChange={setPresenting} title={meta?.title ?? "Strategy presentation"} modalClassName="strategy-present-modal" overlayClassName="strategy-present-overlay"><div className={`strategy-deck strategy-present${sourcesClass}`}><PresentFrame>{renderSlide(slide, true, true)}</PresentFrame><div className="strategy-stage-footer"><span>The story · 16:9</span>{pager}</div></div></KitModal>
    {brandId && mediaSlot && onMediaChange && <KitModal isOpen onOpenChange={(open) => { if (!open) setMediaSlot(null); }} title={`Picture for ${slide.title}`} description="Choose one image or video for this slot." modalClassName="max-w-[860px]">
      <KitSegmentedControl options={[{ value: "team", label: "Brand media" }, { value: "references", label: "References" }, { value: "trends", label: "Trends" }]} value={source} onChange={setSource} ariaLabel="Media source" />
      <div className="mt-5">{source === "team" ? <MediaPickerBody brandId={brandId} isOpen tab={mediaTab} onTabChange={setMediaTab} mode="select" defaultAspect="9:16" onSelect={(item) => void attach({ slotKey: mediaSlot, mediaId: item.id })} onClose={() => setMediaSlot(null)} canWrite /> : <ExternalMediaPicker sourceOverride={source === "trends" ? "trends" : "references"} brandId={brandId} onPick={(pick) => void attach({ slotKey: mediaSlot, ...(pick.kind === "reference" ? { referenceId: pick.referenceId } : { trendExampleId: pick.trendExampleId }) })} />}</div>
    </KitModal>}
  </div>;
};

/** Present: one slide filling the presentation dialog's width. */
function PresentFrame({ children }: { children: ReactNode }) {
  const [ref, box] = useBox<HTMLDivElement>();
  return <div ref={ref} className="strategy-slide-frame">{box.width > 0 && <MiniSlide width={box.width}>{children}</MiniSlide>}</div>;
}

export default StrategyDocumentView;
